/**
 * Alshallal Pastry Company HR System - Admin Console Page
 * Phase 10: Super Admin Console
 * 
 * Provides system diagnostics, user administration, audit explorer, and system status.
 * Super Admin only.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Activity,
  Database,
  Shield,
  Users,
  FileText,
  Mail,
  Bell,
  Lock,
  Unlock,
  Search,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  HardDrive,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Key,
  KeyRound,
  UserCog,
  AtSign,
  Wrench
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ============== Status Indicators ==============

const StatusBadge = ({ status }) => {
  const colors = {
    green: 'bg-green-100 text-green-800 border-green-200',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    red: 'bg-red-100 text-red-800 border-red-200'
  };
  
  const icons = {
    green: <CheckCircle2 className="h-3 w-3" />,
    yellow: <AlertTriangle className="h-3 w-3" />,
    red: <XCircle className="h-3 w-3" />
  };
  
  return (
    <Badge variant="outline" className={`${colors[status]} flex items-center gap-1`}>
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

// ============== Diagnostics Tab ==============

const DiagnosticsTab = () => {
  const { t, isRTL } = useLanguage();
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testReason, setTestReason] = useState('');
  
  const fetchDiagnostics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/diagnostics`);
      setDiagnostics(response.data);
    } catch (error) {
      toast.error(isRTL ? 'فشل في جلب التشخيصات' : 'Failed to fetch diagnostics');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchDiagnostics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const handleTestSmtp = async () => {
    if (!testEmail || testReason.length < 10) {
      toast.error(isRTL ? 'يرجى إدخال البريد الإلكتروني والسبب (10 أحرف على الأقل)' : 'Please enter email and reason (min 10 chars)');
      return;
    }
    
    setTestingSmtp(true);
    try {
      const response = await axios.post(`${API_URL}/admin/test-smtp`, {
        recipient_email: testEmail,
        reason: testReason
      });
      
      if (response.data.success) {
        toast.success(isRTL ? 'تم إرسال البريد الإلكتروني التجريبي' : 'Test email sent successfully');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail?.error || 'Failed to send test email');
    } finally {
      setTestingSmtp(false);
    }
  };
  
  const serviceIcons = {
    database: <Database className="h-5 w-5" />,
    jwt: <Shield className="h-5 w-5" />,
    encryption: <Lock className="h-5 w-5" />,
    smtp: <Mail className="h-5 w-5" />,
    notifications: <Bell className="h-5 w-5" />,
    storage: <HardDrive className="h-5 w-5" />,
    weekend_settings: <Calendar className="h-5 w-5" />
  };
  
  const serviceNames = {
    database: { en: 'Database', ar: 'قاعدة البيانات' },
    jwt: { en: 'JWT Authentication', ar: 'مصادقة JWT' },
    encryption: { en: 'Data Encryption', ar: 'تشفير البيانات' },
    smtp: { en: 'SMTP / Email', ar: 'البريد الإلكتروني' },
    notifications: { en: 'Notifications', ar: 'الإشعارات' },
    storage: { en: 'File Storage', ar: 'تخزين الملفات' },
    weekend_settings: { en: 'Weekend Settings', ar: 'إعدادات عطلة نهاية الأسبوع' }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">
            {isRTL ? 'تشخيصات النظام' : 'System Diagnostics'}
          </h2>
          <p className="text-sm text-slate-500">
            {isRTL ? 'آخر تحديث: ' : 'Last updated: '}
            {diagnostics?.generated_at ? new Date(diagnostics.generated_at).toLocaleString() : '-'}
          </p>
        </div>
        <Button variant="outline" onClick={fetchDiagnostics}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {isRTL ? 'تحديث' : 'Refresh'}
        </Button>
      </div>
      
      {/* Warnings */}
      {diagnostics?.warnings?.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-yellow-800">{isRTL ? 'تحذيرات' : 'Warnings'}</p>
                <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside">
                  {diagnostics.warnings.map((warning, i) => (
                    <li key={i}>{warning}</li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {diagnostics?.services && Object.entries(diagnostics.services).map(([key, service]) => (
          <Card key={key}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    service.status === 'green' ? 'bg-green-100' :
                    service.status === 'yellow' ? 'bg-yellow-100' : 'bg-red-100'
                  }`}>
                    {serviceIcons[key] || <Activity className="h-5 w-5" />}
                  </div>
                  <div>
                    <p className="font-medium">
                      {isRTL ? serviceNames[key]?.ar : serviceNames[key]?.en || key}
                    </p>
                    <p className="text-sm text-slate-500">{service.message}</p>
                  </div>
                </div>
                <StatusBadge status={service.status} />
              </div>
              {service.details && (
                <div className="mt-3 pt-3 border-t text-sm text-slate-600">
                  {Object.entries(service.details).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-slate-500">{k}:</span>
                      <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Test SMTP */}
      {diagnostics?.services?.smtp?.status === 'green' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{isRTL ? 'اختبار البريد الإلكتروني' : 'Test Email'}</CardTitle>
            <CardDescription>
              {isRTL ? 'إرسال بريد إلكتروني تجريبي للتحقق من إعدادات SMTP' : 'Send a test email to verify SMTP configuration'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{isRTL ? 'البريد الإلكتروني المستلم' : 'Recipient Email'}</Label>
                <Input 
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                />
              </div>
              <div>
                <Label>{isRTL ? 'السبب (10 أحرف على الأقل)' : 'Reason (min 10 chars)'}</Label>
                <Input 
                  value={testReason}
                  onChange={(e) => setTestReason(e.target.value)}
                  placeholder={isRTL ? 'اختبار إعدادات SMTP للنظام' : 'Testing system SMTP configuration'}
                />
              </div>
            </div>
            <Button onClick={handleTestSmtp} disabled={testingSmtp}>
              {testingSmtp ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              {isRTL ? 'إرسال بريد تجريبي' : 'Send Test Email'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== Users Tab ==============

const UsersTab = () => {
  const { t, isRTL } = useLanguage();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Lock/Unlock dialog
  const [actionDialog, setActionDialog] = useState({ open: false, user: null, action: null });
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Role change dialog
  const [roleDialog, setRoleDialog] = useState({ open: false, user: null });
  const [newRole, setNewRole] = useState('');
  const [roleReason, setRoleReason] = useState('');
  
  // Password change dialog (Super Admin / HR Manager)
  const [passwordDialog, setPasswordDialog] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordReason, setPasswordReason] = useState('');
  const [forceChangeNextLogin, setForceChangeNextLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  // Email change dialog (Super Admin / HR Manager)
  const [emailDialog, setEmailDialog] = useState({ open: false, user: null });
  const [newEmail, setNewEmail] = useState('');
  const [emailReason, setEmailReason] = useState('');

  // Bulk reset all passwords to Shallal123! (Super Admin only)
  const [bulkResetDialog, setBulkResetDialog] = useState(false);
  const [bulkResetReason, setBulkResetReason] = useState('');
  const [bulkResetConfirm, setBulkResetConfirm] = useState('');
  const [bulkResetIncludeSelf, setBulkResetIncludeSelf] = useState(false);
  const [bulkResetLoading, setBulkResetLoading] = useState(false);

  // Repair Identity Sync
  const [repairLoading, setRepairLoading] = useState(false);
  
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '20'
      });
      
      if (searchQuery) params.append('q', searchQuery);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      
      const response = await axios.get(`${API_URL}/admin/users/search?${params}`);
      setUsers(response.data.users);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error(isRTL ? 'فشل في جلب المستخدمين' : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, roleFilter, isRTL]);
  
  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchUsers]);
  
  const handleLockUnlock = async () => {
    if (actionReason.length < 10) {
      toast.error(isRTL ? 'السبب يجب أن يكون 10 أحرف على الأقل' : 'Reason must be at least 10 characters');
      return;
    }
    
    setActionLoading(true);
    try {
      const endpoint = actionDialog.action === 'lock' 
        ? `/admin/users/${actionDialog.user.id}/lock`
        : `/admin/users/${actionDialog.user.id}/unlock`;
      
      await axios.post(`${API_URL}${endpoint}`, { reason: actionReason });
      
      toast.success(isRTL 
        ? (actionDialog.action === 'lock' ? 'تم قفل الحساب' : 'تم فتح الحساب')
        : (actionDialog.action === 'lock' ? 'Account locked' : 'Account unlocked')
      );
      
      setActionDialog({ open: false, user: null, action: null });
      setActionReason('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail?.error || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleRoleChange = async () => {
    if (roleReason.length < 10) {
      toast.error(isRTL ? 'السبب يجب أن يكون 10 أحرف على الأقل' : 'Reason must be at least 10 characters');
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/admin/users/${roleDialog.user.id}/change-role?new_role=${newRole}&reason=${encodeURIComponent(roleReason)}`
      );
      
      toast.success(isRTL ? 'تم تغيير الدور' : 'Role changed successfully');
      setRoleDialog({ open: false, user: null });
      setNewRole('');
      setRoleReason('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail?.error || 'Role change failed');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error(isRTL ? 'كلمة المرور يجب ألا تقل عن 8 أحرف' : 'Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/\d/.test(newPassword)) {
      toast.error(isRTL 
        ? 'كلمة المرور يجب أن تحتوي على حرف كبير ورقم واحد على الأقل' 
        : 'Password must include at least one uppercase letter and one digit');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(isRTL ? 'كلمتا المرور غير متطابقتين' : 'Passwords do not match');
      return;
    }
    if (passwordReason.length < 10) {
      toast.error(isRTL ? 'السبب يجب أن يكون 10 أحرف على الأقل' : 'Reason must be at least 10 characters');
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/admin/users/${passwordDialog.user.id}/change-password`,
        {
          new_password: newPassword,
          reason: passwordReason,
          force_change_on_next_login: forceChangeNextLogin
        }
      );
      
      toast.success(isRTL ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully');
      setPasswordDialog({ open: false, user: null });
      setNewPassword('');
      setConfirmPassword('');
      setPasswordReason('');
      setForceChangeNextLogin(true);
      setShowPassword(false);
    } catch (error) {
      toast.error(error.response?.data?.detail?.error || 'Password change failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEmailChange = async () => {
    const trimmed = (newEmail || '').trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error(isRTL ? 'صيغة البريد الإلكتروني غير صالحة' : 'Invalid email format');
      return;
    }
    if (emailReason.length < 10) {
      toast.error(isRTL ? 'السبب يجب أن يكون 10 أحرف على الأقل' : 'Reason must be at least 10 characters');
      return;
    }

    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/admin/users/${emailDialog.user.id}/change-email`,
        { new_email: trimmed, reason: emailReason }
      );
      if (res.data?.no_change) {
        toast.info(isRTL ? 'البريد الإلكتروني لم يتغير' : 'Email unchanged');
      } else {
        toast.success(isRTL ? 'تم تغيير البريد الإلكتروني بنجاح' : 'Login email changed successfully');
      }
      setEmailDialog({ open: false, user: null });
      setNewEmail('');
      setEmailReason('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail?.error || 'Email change failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRepairIdentitySync = async () => {
    if (!window.confirm(isRTL
      ? 'هل تريد فحص جميع المستخدمين وإصلاح روابط الموظفين؟ سيتم نسخ رقم الهوية / الإقامة المخزن على ملف الموظف إلى حساب المستخدم لتمكين تسجيل الدخول.'
      : 'Scan all users and repair employee links? National IDs / Iqamas stored on linked employee records will be copied to the user account so users can log in by ID/Iqama.')) {
      return;
    }
    setRepairLoading(true);
    try {
      const res = await axios.post(`${API_URL}/admin/users/repair-identity-sync?dry_run=false`);
      const s = res.data || {};
      toast.success(
        isRTL
          ? `تم: ${s.users_linked || 0} ربط، ${s.national_id_backfilled || 0} هوية، ${s.iqama_backfilled || 0} إقامة، ${s.email_aligned || 0} بريد`
          : `Repaired: ${s.users_linked || 0} linked, ${s.national_id_backfilled || 0} NIDs, ${s.iqama_backfilled || 0} Iqamas, ${s.email_aligned || 0} emails`,
        { duration: 6000 }
      );
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail?.error || 'Repair failed');
    } finally {
      setRepairLoading(false);
    }
  };

  const handleBulkResetPasswords = async () => {
    if (bulkResetConfirm.trim() !== 'RESET-ALL-PASSWORDS') {
      toast.error(isRTL
        ? 'يجب كتابة عبارة التأكيد بالضبط: RESET-ALL-PASSWORDS'
        : 'You must type the exact confirmation phrase: RESET-ALL-PASSWORDS');
      return;
    }
    if (bulkResetReason.trim().length < 10) {
      toast.error(isRTL ? 'السبب يجب أن يكون 10 أحرف على الأقل' : 'Reason must be at least 10 characters');
      return;
    }
    setBulkResetLoading(true);
    try {
      const res = await axios.post(`${API_URL}/admin/users/reset-all-passwords`, {
        reason: bulkResetReason,
        include_self: bulkResetIncludeSelf,
        confirm: bulkResetConfirm.trim(),
      });
      const s = res.data || {};
      toast.success(
        isRTL
          ? `تم إعادة تعيين ${s.reset_count} كلمة مرور إلى Shallal123!. سيُطلب من كل مستخدم تغييرها عند تسجيل الدخول التالي.`
          : `Reset ${s.reset_count} password(s) to Shallal123!. Each user will be forced to change it on next login.`,
        { duration: 8000 }
      );
      setBulkResetDialog(false);
      setBulkResetReason('');
      setBulkResetConfirm('');
      setBulkResetIncludeSelf(false);
    } catch (error) {
      toast.error(error.response?.data?.detail?.error || 'Bulk password reset failed');
    } finally {
      setBulkResetLoading(false);
    }
  };
  
  const roleLabels = {
    super_admin: { en: 'Super Admin', ar: 'مدير النظام' },
    it_inf: { en: 'IT INF', ar: 'تقنية المعلومات والبنية التحتية' },
    hr_admin: { en: 'HR Admin', ar: 'مدير الموارد البشرية' },
    hr_manager: { en: 'HR Manager', ar: 'مدير HR' },
    hr_assistant: { en: 'HR Assistant', ar: 'مساعد الموارد البشرية' },
    manager: { en: 'Manager', ar: 'مدير' },
    employee: { en: 'Employee', ar: 'موظف' }
  };
  
  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input 
            placeholder={isRTL ? 'البحث بالاسم أو البريد الإلكتروني...' : 'Search by name or email...'}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            className="pl-10"
          />
        </div>
        <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]" data-testid="admin-console-role-filter">
            <SelectValue placeholder={isRTL ? 'جميع الأدوار' : 'All Roles'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isRTL ? 'جميع الأدوار' : 'All Roles'}</SelectItem>
            {Object.entries(roleLabels).map(([key, label]) => (
              <SelectItem key={key} value={key} data-testid={`admin-console-role-filter-${key}`}>
                {isRTL ? label.ar : label.en}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={handleRepairIdentitySync}
          disabled={repairLoading}
          className="border-brand-orange/40 text-brand-brown hover:bg-brand-orange/10"
          data-testid="repair-identity-sync-btn"
          title={isRTL
            ? 'إصلاح روابط الموظف ↔ المستخدم وتمكين تسجيل الدخول بالهوية / الإقامة لكل المستخدمين'
            : 'Repair employee ↔ user links and enable National ID / Iqama login for all users'}
        >
          {repairLoading
            ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            : <Wrench className="h-4 w-4 mr-2" />}
          {isRTL ? 'إصلاح ربط الهوية' : 'Repair Identity Sync'}
        </Button>
        {user?.role === 'super_admin' && (
          <Button
            variant="outline"
            onClick={() => setBulkResetDialog(true)}
            className="border-brand-brown/40 text-brand-brown hover:bg-brand-brown/10"
            data-testid="reset-all-passwords-btn"
            title={isRTL
              ? 'إعادة تعيين كلمة مرور جميع المستخدمين إلى Shallal123! وإجبارهم على تغييرها عند تسجيل الدخول التالي'
              : 'Reset every user\'s password to Shallal123! and force change on next login'}
          >
            <KeyRound className="h-4 w-4 mr-2" />
            {isRTL ? 'إعادة تعيين كلمات المرور' : 'Reset All Passwords'}
          </Button>
        )}
      </div>
      
      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{isRTL ? 'المستخدم' : 'User'}</TableHead>
                <TableHead>{isRTL ? 'البريد الإلكتروني' : 'Email'}</TableHead>
                <TableHead>{isRTL ? 'الدور' : 'Role'}</TableHead>
                <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                <TableHead>{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    {isRTL ? 'لم يتم العثور على مستخدمين' : 'No users found'}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
                          <Users className="h-4 w-4 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium">{isRTL ? `${user.first_name_ar || user.first_name} ${user.last_name_ar || user.last_name}` : `${user.first_name} ${user.last_name}`}</p>
                          <p className="text-sm text-slate-500">ID: {user.id.slice(0, 8)}...</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {isRTL ? roleLabels[user.role]?.ar : roleLabels[user.role]?.en || user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.is_locked ? (
                        <Badge variant="destructive">{isRTL ? 'مقفل' : 'Locked'}</Badge>
                      ) : user.is_active ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700">{isRTL ? 'نشط' : 'Active'}</Badge>
                      ) : (
                        <Badge variant="secondary">{isRTL ? 'غير نشط' : 'Inactive'}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {user.is_locked ? (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setActionDialog({ open: true, user, action: 'unlock' })}
                          >
                            <Unlock className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setActionDialog({ open: true, user, action: 'lock' })}
                          >
                            <Lock className="h-4 w-4" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => { setRoleDialog({ open: true, user }); setNewRole(user.role); }}
                          data-testid={`change-role-btn-${user.id}`}
                          title={isRTL ? 'تغيير الدور' : 'Change Role'}
                        >
                          <UserCog className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => { setEmailDialog({ open: true, user }); setNewEmail(user.email || ''); }}
                          data-testid={`change-email-btn-${user.id}`}
                          title={isRTL ? 'تغيير البريد الإلكتروني (تسجيل الدخول)' : 'Change login email'}
                        >
                          <AtSign className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setPasswordDialog({ open: true, user })}
                          data-testid={`change-password-btn-${user.id}`}
                          title={isRTL ? 'تغيير كلمة المرور' : 'Change Password'}
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {isRTL ? `عرض ${users.length} من ${total}` : `Showing ${users.length} of ${total}`}
        </p>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="px-3 py-2 text-sm">{page} / {totalPages || 1}</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Lock/Unlock Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, user: null, action: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.action === 'lock' 
                ? (isRTL ? 'قفل الحساب' : 'Lock Account')
                : (isRTL ? 'فتح الحساب' : 'Unlock Account')
              }
            </DialogTitle>
            <DialogDescription>
              {actionDialog.action === 'lock'
                ? (isRTL ? 'سيتم منع المستخدم من تسجيل الدخول' : 'The user will be prevented from logging in')
                : (isRTL ? 'سيتمكن المستخدم من تسجيل الدخول مرة أخرى' : 'The user will be able to log in again')
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{isRTL ? 'المستخدم' : 'User'}</Label>
              <p className="text-sm text-slate-600">{actionDialog.user?.email}</p>
            </div>
            <div>
              <Label>{isRTL ? 'السبب (مطلوب - 10 أحرف على الأقل)' : 'Reason (required - min 10 chars)'}</Label>
              <Textarea 
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder={isRTL ? 'أدخل سبب هذا الإجراء...' : 'Enter reason for this action...'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, user: null, action: null })}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleLockUnlock} 
              disabled={actionLoading || actionReason.length < 10}
              variant={actionDialog.action === 'lock' ? 'destructive' : 'default'}
            >
              {actionLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              {actionDialog.action === 'lock' ? (isRTL ? 'قفل' : 'Lock') : (isRTL ? 'فتح' : 'Unlock')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Role Change Dialog */}
      <Dialog open={roleDialog.open} onOpenChange={(open) => !open && setRoleDialog({ open: false, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isRTL ? 'تغيير الدور' : 'Change Role'}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'تغيير دور المستخدم في النظام' : 'Change the user\'s role in the system'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{isRTL ? 'المستخدم' : 'User'}</Label>
              <p className="text-sm text-slate-600">{roleDialog.user?.email}</p>
            </div>
            <div>
              <Label>{isRTL ? 'الدور الجديد' : 'New Role'}</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger data-testid="admin-console-new-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(roleLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key} data-testid={`admin-console-new-role-${key}`}>
                      {isRTL ? label.ar : label.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{isRTL ? 'السبب (مطلوب - 10 أحرف على الأقل)' : 'Reason (required - min 10 chars)'}</Label>
              <Textarea 
                value={roleReason}
                onChange={(e) => setRoleReason(e.target.value)}
                placeholder={isRTL ? 'أدخل سبب تغيير الدور...' : 'Enter reason for role change...'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog({ open: false, user: null })}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleRoleChange} 
              disabled={actionLoading || roleReason.length < 10 || newRole === roleDialog.user?.role}
            >
              {actionLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              {isRTL ? 'تغيير الدور' : 'Change Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Change Password Dialog (Super Admin / HR Manager) */}
      <Dialog
        open={passwordDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setPasswordDialog({ open: false, user: null });
            setNewPassword('');
            setConfirmPassword('');
            setPasswordReason('');
            setForceChangeNextLogin(true);
            setShowPassword(false);
          }
        }}
      >
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>
              {isRTL ? 'تغيير كلمة مرور المستخدم' : 'Change User Password'}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'تعيين كلمة مرور جديدة لهذا المستخدم. سيتم تسجيل هذا الإجراء في سجل التدقيق.' 
                : 'Set a new password for this user. This action is recorded in the audit log.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{isRTL ? 'المستخدم' : 'User'}</Label>
              <p className="text-sm text-slate-600">{passwordDialog.user?.email}</p>
            </div>
            <div>
              <Label>{isRTL ? 'كلمة المرور الجديدة' : 'New Password'}</Label>
              <div className="relative">
                <Input 
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={isRTL ? '8+ أحرف، حرف كبير واحد ورقم واحد على الأقل' : 'Min 8 chars, 1 uppercase, 1 digit'}
                  data-testid="new-password-input"
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label>{isRTL ? 'تأكيد كلمة المرور' : 'Confirm Password'}</Label>
              <Input 
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="confirm-password-input"
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label>{isRTL ? 'السبب (مطلوب - 10 أحرف على الأقل)' : 'Reason (required - min 10 chars)'}</Label>
              <Textarea 
                value={passwordReason}
                onChange={(e) => setPasswordReason(e.target.value)}
                placeholder={isRTL ? 'مثال: طلب إعادة تعيين كلمة المرور من المستخدم' : 'e.g., User requested password reset'}
                data-testid="password-reason-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="force-change-next-login"
                checked={forceChangeNextLogin}
                onChange={(e) => setForceChangeNextLogin(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
                data-testid="force-change-checkbox"
              />
              <Label htmlFor="force-change-next-login" className="cursor-pointer">
                {isRTL 
                  ? 'مطالبة المستخدم بتغيير كلمة المرور في تسجيل الدخول التالي' 
                  : 'Require user to change password on next login'}
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setPasswordDialog({ open: false, user: null })}
              data-testid="cancel-password-change-btn"
            >
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handlePasswordChange} 
              disabled={actionLoading || !newPassword || !confirmPassword || passwordReason.length < 10}
              data-testid="change-password-submit"
            >
              {actionLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              <Key className="h-4 w-4 mr-2" />
              {isRTL ? 'تغيير كلمة المرور' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Email Dialog */}
      <Dialog
        open={emailDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setEmailDialog({ open: false, user: null });
            setNewEmail('');
            setEmailReason('');
          }
        }}
      >
        <DialogContent data-testid="change-email-dialog">
          <DialogHeader>
            <DialogTitle>
              {isRTL ? 'تغيير البريد الإلكتروني (تسجيل الدخول)' : 'Change Login Email'}
            </DialogTitle>
            <DialogDescription>
              {isRTL
                ? 'تعيين بريد إلكتروني جديد لتسجيل دخول هذا المستخدم. سيتم مزامنته أيضاً مع ملف الموظف المرتبط، وتسجيل الإجراء في سجل التدقيق.'
                : 'Set a new login email for this user. The change is also synced to the linked employee profile and recorded in the audit log.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{isRTL ? 'المستخدم' : 'User'}</Label>
              <p className="text-sm text-slate-600">
                {emailDialog.user?.first_name} {emailDialog.user?.last_name}
              </p>
            </div>
            <div>
              <Label>{isRTL ? 'البريد الإلكتروني الحالي' : 'Current Email'}</Label>
              <p className="text-sm text-slate-600 font-mono break-all">
                {emailDialog.user?.email || (isRTL ? '(غير محدد)' : '(none)')}
              </p>
            </div>
            <div>
              <Label htmlFor="new-email-input">
                {isRTL ? 'البريد الإلكتروني الجديد' : 'New Email'}
              </Label>
              <Input
                id="new-email-input"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="name@example.com"
                data-testid="new-email-input"
                autoComplete="off"
                dir="ltr"
              />
            </div>
            <div>
              <Label htmlFor="email-reason-input">
                {isRTL ? 'السبب (مطلوب - 10 أحرف على الأقل)' : 'Reason (required - min 10 chars)'}
              </Label>
              <Textarea
                id="email-reason-input"
                value={emailReason}
                onChange={(e) => setEmailReason(e.target.value)}
                placeholder={isRTL
                  ? 'مثال: تحديث البريد الإلكتروني للموظف بعد طلب الموارد البشرية'
                  : 'e.g., Updating employee email per HR request'}
                data-testid="email-reason-input"
              />
            </div>
            <div className="rounded-md border border-brand-orange/30 bg-brand-orange/10 p-3 text-xs text-brand-brown leading-relaxed">
              {isRTL
                ? 'ملاحظة: سيتمكن المستخدم من تسجيل الدخول بالبريد الإلكتروني الجديد فوراً. كما يمكنه الاستمرار في تسجيل الدخول برقم الهوية أو الإقامة.'
                : 'Note: the user can log in with the new email immediately. National ID / Iqama login continues to work.'}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailDialog({ open: false, user: null })}
              data-testid="cancel-email-change-btn"
            >
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleEmailChange}
              disabled={actionLoading || !newEmail || emailReason.length < 10}
              data-testid="change-email-submit"
            >
              {actionLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              <AtSign className="h-4 w-4 mr-2" />
              {isRTL ? 'تغيير البريد الإلكتروني' : 'Change Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Reset All Passwords Dialog */}
      <Dialog
        open={bulkResetDialog}
        onOpenChange={(open) => {
          if (!open) {
            setBulkResetDialog(false);
            setBulkResetReason('');
            setBulkResetConfirm('');
            setBulkResetIncludeSelf(false);
          }
        }}
      >
        <DialogContent data-testid="bulk-reset-passwords-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-brand-brown">
              <KeyRound className="h-5 w-5" />
              {isRTL ? 'إعادة تعيين كلمات المرور لجميع المستخدمين' : 'Reset All User Passwords'}
            </DialogTitle>
            <DialogDescription>
              {isRTL
                ? 'سيتم تعيين كلمة المرور Shallal123! لكل مستخدم في النظام، وسيُطلب من كل مستخدم تغييرها فور تسجيل الدخول التالي.'
                : 'Every user\'s password will be set to Shallal123! and they will be forced to change it on next login.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border-2 border-brand-brown/30 bg-brand-brown/8 p-3 text-sm text-brand-brown leading-relaxed font-medium">
              {isRTL
                ? '⚠️ هذا إجراء حساس ولا يمكن التراجع عنه. يتم تسجيل كل عملية إعادة تعيين في سجل التدقيق ونسخها إلى R2.'
                : '⚠️ This is a sensitive, irreversible operation. Every reset is recorded in the audit log and mirrored to R2.'}
            </div>
            <div>
              <Label htmlFor="bulk-reset-reason">
                {isRTL ? 'السبب (10 أحرف على الأقل)' : 'Reason (min 10 chars)'}
              </Label>
              <Textarea
                id="bulk-reset-reason"
                value={bulkResetReason}
                onChange={(e) => setBulkResetReason(e.target.value)}
                placeholder={isRTL
                  ? 'مثال: توحيد كلمة المرور الأولية لجميع المستخدمين'
                  : 'e.g., Unify initial password for all users across the system'}
                data-testid="bulk-reset-reason-input"
              />
            </div>
            <div>
              <Label htmlFor="bulk-reset-confirm">
                {isRTL
                  ? 'اكتب RESET-ALL-PASSWORDS للتأكيد'
                  : 'Type RESET-ALL-PASSWORDS to confirm'}
              </Label>
              <Input
                id="bulk-reset-confirm"
                type="text"
                value={bulkResetConfirm}
                onChange={(e) => setBulkResetConfirm(e.target.value)}
                placeholder="RESET-ALL-PASSWORDS"
                data-testid="bulk-reset-confirm-input"
                autoComplete="off"
                dir="ltr"
                className="font-mono"
              />
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={bulkResetIncludeSelf}
                onChange={(e) => setBulkResetIncludeSelf(e.target.checked)}
                className="mt-1 accent-brand-orange"
                data-testid="bulk-reset-include-self"
              />
              <span className="text-sm text-muted-foreground leading-snug">
                {isRTL
                  ? 'تضمين حسابي أيضًا (سيتم إعادة تعيين كلمة مرورك إلى Shallal123! - يجب عليك معرفة هذا قبل المتابعة).'
                  : 'Also reset my account (your password will become Shallal123! — you must know this before continuing).'}
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkResetDialog(false)}
              data-testid="cancel-bulk-reset-btn"
            >
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkResetPasswords}
              disabled={bulkResetLoading || bulkResetConfirm.trim() !== 'RESET-ALL-PASSWORDS' || bulkResetReason.length < 10}
              data-testid="bulk-reset-submit"
            >
              {bulkResetLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              <KeyRound className="h-4 w-4 mr-2" />
              {isRTL ? 'إعادة تعيين الآن' : 'Reset Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== Audit Logs Tab ==============

const AuditLogsTab = () => {
  const { isRTL } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionSummary, setActionSummary] = useState({});
  const [categories, setCategories] = useState([]);
  
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '30'
      });
      
      if (category !== 'all') params.append('action_category', category);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(`${API_URL}/admin/audit-explorer?${params}`);
      setLogs(response.data.logs);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
      setActionSummary(response.data.action_summary || {});
      setCategories(response.data.available_categories || []);
    } catch (error) {
      toast.error(isRTL ? 'فشل في جلب السجلات' : 'Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  }, [page, category, dateFrom, dateTo, isRTL]);
  
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  
  const categoryLabels = {
    auth: { en: 'Authentication', ar: 'المصادقة' },
    user: { en: 'User Management', ar: 'إدارة المستخدمين' },
    employee: { en: 'Employee', ar: 'الموظفين' },
    document: { en: 'Documents', ar: 'المستندات' },
    leave: { en: 'Leave', ar: 'الإجازات' },
    request: { en: 'Requests', ar: 'الطلبات' },
    export: { en: 'Exports', ar: 'التصدير' },
    import: { en: 'Imports', ar: 'الاستيراد' },
    security: { en: 'Security', ar: 'الأمان' },
    system: { en: 'System', ar: 'النظام' }
  };
  
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={isRTL ? 'جميع الفئات' : 'All Categories'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isRTL ? 'جميع الفئات' : 'All Categories'}</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {isRTL ? categoryLabels[cat]?.ar : categoryLabels[cat]?.en || cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input 
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          className="w-[180px]"
        />
        <Input 
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          className="w-[180px]"
        />
        <Button variant="outline" onClick={() => { setCategory('all'); setDateFrom(''); setDateTo(''); setPage(1); }}>
          {isRTL ? 'مسح' : 'Clear'}
        </Button>
      </div>
      
      {/* Summary */}
      {Object.keys(actionSummary).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(actionSummary).slice(0, 5).map(([action, count]) => (
            <Badge key={action} variant="secondary">
              {action}: {count}
            </Badge>
          ))}
        </div>
      )}
      
      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{isRTL ? 'الوقت' : 'Time'}</TableHead>
                <TableHead>{isRTL ? 'الإجراء' : 'Action'}</TableHead>
                <TableHead>{isRTL ? 'الفاعل' : 'Actor'}</TableHead>
                <TableHead>{isRTL ? 'الهدف' : 'Target'}</TableHead>
                <TableHead>{isRTL ? 'السبب' : 'Reason'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    {isRTL ? 'لم يتم العثور على سجلات' : 'No logs found'}
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm">
                      {new Date(log.timestamp).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{log.action}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{log.actor_email || log.actor_id?.slice(0, 8)}</TableCell>
                    <TableCell className="text-sm">
                      {log.target_type && `${log.target_type}: ${log.target_id?.slice(0, 8) || '-'}`}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">
                      {log.reason || '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {isRTL ? `عرض ${logs.length} من ${total}` : `Showing ${logs.length} of ${total}`}
        </p>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="px-3 py-2 text-sm">{page} / {totalPages || 1}</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============== System Status Tab ==============

const SystemStatusTab = () => {
  const { isRTL } = useLanguage();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/system-status`);
      setStatus(response.data);
    } catch (error) {
      toast.error(isRTL ? 'فشل في جلب حالة النظام' : 'Failed to fetch system status');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">
            {isRTL ? 'حالة النظام' : 'System Status'}
          </h2>
          <p className="text-sm text-slate-500">
            {isRTL ? 'آخر تحديث: ' : 'Last updated: '}
            {status?.generated_at ? new Date(status.generated_at).toLocaleString() : '-'}
          </p>
        </div>
        <Button variant="outline" onClick={fetchStatus}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {isRTL ? 'تحديث' : 'Refresh'}
        </Button>
      </div>
      
      {/* Database Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="h-5 w-5" />
            {isRTL ? 'قاعدة البيانات' : 'Database'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status?.database?.status === 'connected' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {status.database.collections && Object.entries(status.database.collections).map(([name, count]) => (
                <div key={name} className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{count}</p>
                  <p className="text-sm text-slate-500 capitalize">{name.replace('_', ' ')}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-red-600">{isRTL ? 'خطأ في الاتصال' : 'Connection error'}</p>
          )}
        </CardContent>
      </Card>
      
      {/* Storage */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            {isRTL ? 'التخزين' : 'Storage'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status?.storage?.status === 'available' ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">{isRTL ? 'الحالة' : 'Status'}</p>
                <Badge variant="outline" className="bg-green-50 text-green-700 mt-1">
                  {isRTL ? 'متاح' : 'Available'}
                </Badge>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">{isRTL ? 'قابل للكتابة' : 'Writable'}</p>
                <p className="font-medium">{status.storage.writable ? (isRTL ? 'نعم' : 'Yes') : (isRTL ? 'لا' : 'No')}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">{isRTL ? 'عدد الملفات' : 'File Count'}</p>
                <p className="font-medium">{status.storage.file_count}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">{isRTL ? 'الحجم الكلي' : 'Total Size'}</p>
                <p className="font-medium">{status.storage.total_size_mb} MB</p>
              </div>
            </div>
          ) : (
            <p className="text-yellow-600">{isRTL ? 'التخزين غير مهيأ' : 'Storage not initialized'}</p>
          )}
        </CardContent>
      </Card>
      
      {/* Scheduled Tasks */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {isRTL ? 'المهام المجدولة' : 'Scheduled Tasks'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status?.scheduled_tasks && Object.entries(status.scheduled_tasks).map(([name, task]) => (
            <div key={name} className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium">{task.description}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {isRTL ? 'نقطة النهاية: ' : 'Endpoint: '}<code className="bg-slate-200 px-1 rounded">{task.endpoint}</code>
                  </p>
                  <p className="text-sm text-slate-500">
                    {isRTL ? 'الجدول الموصى به: ' : 'Recommended: '}{task.recommended_schedule}
                  </p>
                </div>
                <Badge variant="outline">{task.status}</Badge>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// ============== Main Admin Console Page ==============

const AdminConsolePage = () => {
  const { user } = useAuth();
  const { t, isRTL } = useLanguage();
  
  // 2026-06 matrix: the console holds two different jobs, and they no longer
  // belong to the same people. Diagnostics and Status are the system side
  // (Super Admin, IT INF). Users and Audit are account administration
  // (Super Admin, HR Manager, IT INF).
  const role = user?.role;
  const canSeeSystemTabs = role === 'super_admin' || role === 'it_inf';
  const canSeeAccountTabs = canSeeSystemTabs || role === 'hr_manager';
  const [activeTab, setActiveTab] = useState(
    canSeeSystemTabs ? 'diagnostics' : 'users'
  );
  
  const canAccessAdminConsole = canSeeAccountTabs;
  if (!canAccessAdminConsole) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="p-4 bg-red-100 rounded-full w-fit mx-auto mb-6">
              <Shield className="h-10 w-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              {isRTL ? 'غير مصرح' : 'Access Denied'}
            </h1>
            <p className="text-slate-600">
              {isRTL 
                ? 'هذه الصفحة متاحة لمديري النظام (Super Admin) ومديري الموارد البشرية (HR Manager) وتقنية المعلومات (IT INF)'
                : 'This page is only accessible to Super Admin, HR Manager and IT INF'
              }
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          {isRTL ? 'لوحة تحكم المسؤول' : 'Admin Console'}
        </h1>
        <p className="text-slate-500">
          {isRTL ? 'إدارة النظام والحوكمة' : 'System administration and governance'}
        </p>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-flex sm:grid-cols-4">
          {canSeeSystemTabs && (
            <TabsTrigger value="diagnostics" className="flex items-center gap-2" data-testid="admin-tab-diagnostics">
              <Activity className="h-4 w-4" />
              <span className="hidden sm:inline">{isRTL ? 'التشخيصات' : 'Diagnostics'}</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="users" className="flex items-center gap-2" data-testid="admin-tab-users">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">{isRTL ? 'المستخدمين' : 'Users'}</span>
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2" data-testid="admin-tab-audit">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">{isRTL ? 'السجلات' : 'Audit Logs'}</span>
          </TabsTrigger>
          {canSeeSystemTabs && (
            <TabsTrigger value="status" className="flex items-center gap-2" data-testid="admin-tab-status">
              <Database className="h-4 w-4" />
              <span className="hidden sm:inline">{isRTL ? 'الحالة' : 'Status'}</span>
            </TabsTrigger>
          )}
        </TabsList>
        
        {canSeeSystemTabs && (
          <TabsContent value="diagnostics" className="mt-6">
            <DiagnosticsTab />
          </TabsContent>
        )}
        
        <TabsContent value="users" className="mt-6">
          <UsersTab />
        </TabsContent>
        
        <TabsContent value="audit" className="mt-6">
          <AuditLogsTab />
        </TabsContent>
        
        {canSeeSystemTabs && (
          <TabsContent value="status" className="mt-6">
            <SystemStatusTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default AdminConsolePage;
